import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { Eye, EyeOff, Copy, Check, Upload, Plus, X } from 'lucide-react';
import { countries, getCountryNames } from '@/lib/countries';

type Step = 1 | 2 | 3;

interface PersonalDetails {
  firstName: string;
  middleName: string;
  surname: string;
  email: string;
  mobile: string;
  idType: 'id' | 'drivers_license' | 'passport';
  idNumber: string;
  country: string;
  city: string;
  zipcode: string;
  personalWebsite: string;
  linkedinProfile: string;
}

interface SchoolDetails {
  schoolName: string;
  address: string;
  country: string;
  city: string;
  zipcode: string;
  organizationId: string;
  organizationPhone: string;
  organizationEmail: string;
  organizationWebsite: string;
  isPhysicalSchool: boolean;
}

interface FinalDetails {
  primaryColor: string;
  secondaryColor: string;
  logo: File | null;
  schoolOwnership: 'private' | 'public';
  schoolPurpose: string[];
  teammates: { email: string; role: 'Admin' | 'Editor' | 'Viewer' }[];
}

interface FormData {
  personal: PersonalDetails;
  school: SchoolDetails;
  final: FinalDetails;
  password: string;
}

const initialFormData: FormData = {
  personal: {
    firstName: '',
    middleName: '',
    surname: '',
    email: '',
    mobile: '',
    idType: 'id',
    idNumber: '',
    country: '',
    city: '',
    zipcode: '',
    personalWebsite: '',
    linkedinProfile: '',
  },
  school: {
    schoolName: '',
    address: '',
    country: '',
    city: '',
    zipcode: '',
    organizationId: '',
    organizationPhone: '',
    organizationEmail: '',
    organizationWebsite: '',
    isPhysicalSchool: true,
  },
  final: {
    primaryColor: '#14b8a6',
    secondaryColor: '#f97316',
    logo: null,
    schoolOwnership: 'private',
    schoolPurpose: [],
    teammates: [{ email: '', role: 'Admin' }],
  },
  password: '',
};

const schoolPurposeOptions = [
  'K-12',
  'Middle School',
  'Highschool',
  'College',
  'University',
  'Technical School',
  'Online School',
];

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const generatedPassword = 'SecureP@ss123!';

  const getProgress = () => {
    switch (step) {
      case 1:
        return 31;
      case 2:
        return 67;
      case 3:
        return 99;
      default:
        return 0;
    }
  };

  const getStepLabel = () => {
    switch (step) {
      case 1:
        return 'Personal Details';
      case 2:
        return "School's Details";
      case 3:
        return 'Final Details';
      default:
        return '';
    }
  };

  const handlePersonalChange = (field: keyof PersonalDetails, value: string) => {
    setFormData((prev) => ({
      ...prev,
      personal: { ...prev.personal, [field]: value },
    }));
  };

  const handleSchoolChange = (field: keyof SchoolDetails, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      school: { ...prev.school, [field]: value },
    }));
  };

  const handleFinalChange = (field: keyof FinalDetails, value: any) => {
    setFormData((prev) => ({
      ...prev,
      final: { ...prev.final, [field]: value },
    }));
  };

  const toggleSchoolPurpose = (purpose: string) => {
    const current = formData.final.schoolPurpose;
    if (current.includes(purpose)) {
      handleFinalChange(
        'schoolPurpose',
        current.filter((p) => p !== purpose)
      );
    } else {
      handleFinalChange('schoolPurpose', [...current, purpose]);
    }
  };

  const addTeammate = () => {
    handleFinalChange('teammates', [
      ...formData.final.teammates,
      { email: '', role: 'Admin' as const },
    ]);
  };

  const removeTeammate = (index: number) => {
    handleFinalChange(
      'teammates',
      formData.final.teammates.filter((_, i) => i !== index)
    );
  };

  const updateTeammate = (index: number, field: 'email' | 'role', value: string) => {
    const updated = [...formData.final.teammates];
    updated[index] = { ...updated[index], [field]: value };
    handleFinalChange('teammates', updated);
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const handleNext = () => {
    if (step === 1) {
      // Auto-populate organization fields from personal details
      setFormData((prev) => ({
        ...prev,
        school: {
          ...prev.school,
          organizationEmail: prev.school.organizationEmail || prev.personal.email,
          organizationPhone: prev.school.organizationPhone || prev.personal.mobile,
          country: prev.school.country || prev.personal.country,
          city: prev.school.city || prev.personal.city,
        },
      }));
    }
    if (step < 3) {
      setStep((step + 1) as Step);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);

    try {
      await register({
        first_name: formData.personal.firstName,
        last_name: formData.personal.surname,
        email: formData.personal.email,
        password: useCustomPassword ? formData.password : generatedPassword,
        password_confirmation: useCustomPassword ? formData.password : generatedPassword,
        phone: formData.personal.mobile,
        // School data
        school_name: formData.school.schoolName,
        school_address: formData.school.address,
        school_city: formData.school.city,
        school_country: formData.school.country,
        school_phone: formData.school.organizationPhone,
        school_email: formData.school.organizationEmail,
        is_online_only: !formData.school.isPhysicalSchool,
        // Theme settings
        primary_color: formData.final.primaryColor,
        secondary_color: formData.final.secondaryColor,
        school_ownership: formData.final.schoolOwnership,
        school_purposes: formData.final.schoolPurpose,
      });
      navigate('/');
    } catch (err: any) {
      setError(err.message || err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    console.log('Google sign up');
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">V</span>
          </div>
          <span className="font-semibold text-gray-900">Welcome to VIScool</span>
        </div>
        <Link
          to="/login"
          className="text-sm text-orange-500 hover:text-orange-600 font-medium"
        >
          Log In
        </Link>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-teal-500 transition-all duration-300"
            style={{ width: `${getProgress()}%` }}
          />
        </div>
        <span className="text-sm font-medium text-orange-500">
          {getProgress()}% <span className="text-gray-500">{getStepLabel()}</span>
        </span>
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Sign-Up</h1>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Step 1: Personal Details */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              * Full Name
            </label>
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                value={formData.personal.firstName}
                onChange={(e) => handlePersonalChange('firstName', e.target.value)}
                placeholder="Personal Name"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="text"
                value={formData.personal.middleName}
                onChange={(e) => handlePersonalChange('middleName', e.target.value)}
                placeholder="Middle Name"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="text"
                value={formData.personal.surname}
                onChange={(e) => handlePersonalChange('surname', e.target.value)}
                placeholder="Surname"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Personal Mail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              * Personal Mail
            </label>
            <input
              type="email"
              value={formData.personal.email}
              onChange={(e) => handlePersonalChange('email', e.target.value)}
              placeholder="You will be given a VIS mailbox later"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              dir="ltr"
            />
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              * Mobile Number
            </label>
            <div className="flex gap-2" dir="ltr">
              <select className="w-32 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                {countries.map((country) => (
                  <option key={country.code} value={country.dialCode}>
                    {country.dialCode} ({country.code})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={formData.personal.mobile}
                onChange={(e) => handlePersonalChange('mobile', e.target.value)}
                placeholder="For verifications, we won't spam you"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                dir="ltr"
              />
            </div>
          </div>

          {/* ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ID
            </label>
            <div className="flex items-center gap-4 mb-2">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="radio"
                  name="idType"
                  checked={formData.personal.idType === 'id'}
                  onChange={() => handlePersonalChange('idType', 'id')}
                  className="text-teal-600"
                />
                ID
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="radio"
                  name="idType"
                  checked={formData.personal.idType === 'drivers_license'}
                  onChange={() => handlePersonalChange('idType', 'drivers_license')}
                  className="text-teal-600"
                />
                Drivers License
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="radio"
                  name="idType"
                  checked={formData.personal.idType === 'passport'}
                  onChange={() => handlePersonalChange('idType', 'passport')}
                  className="text-teal-600"
                />
                Passport
              </label>
            </div>
            <input
              type="text"
              value={formData.personal.idNumber}
              onChange={(e) => handlePersonalChange('idNumber', e.target.value)}
              placeholder="Personal identification number"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Later on you will need to upload it
            </p>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <div className="grid grid-cols-3 gap-3">
              <select
                value={formData.personal.country}
                onChange={(e) => handlePersonalChange('country', e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select Country</option>
                {getCountryNames().map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <input
                type="text"
                value={formData.personal.city}
                onChange={(e) => handlePersonalChange('city', e.target.value)}
                placeholder="City"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="text"
                value={formData.personal.zipcode}
                onChange={(e) => handlePersonalChange('zipcode', e.target.value)}
                placeholder="Zipcode"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Some Links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Some Links
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="url"
                value={formData.personal.personalWebsite}
                onChange={(e) => handlePersonalChange('personalWebsite', e.target.value)}
                placeholder="Personal Website"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                dir="ltr"
              />
              <input
                type="url"
                value={formData.personal.linkedinProfile}
                onChange={(e) => handlePersonalChange('linkedinProfile', e.target.value)}
                placeholder="LinkedIn Profile"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                dir="ltr"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            {!useCustomPassword ? (
              <div className="flex items-center gap-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={generatedPassword}
                  readOnly
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={copyPassword}
                  className={`p-2.5 rounded-lg transition-colors ${
                    passwordCopied
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {passwordCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your custom password"
                  className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setUseCustomPassword(!useCustomPassword)}
              className="text-sm text-teal-600 hover:text-teal-700 mt-2"
            >
              {useCustomPassword ? 'Use auto-generated password' : 'Switch to Custom Password'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: School's Details */}
      {step === 2 && (
        <div className="space-y-6">
          {/* School Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schools Name
            </label>
            <input
              type="text"
              value={formData.school.schoolName}
              onChange={(e) => handleSchoolChange('schoolName', e.target.value)}
              placeholder="What will be your School's name"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* School's Address */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                School's Address
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="radio"
                    name="schoolType"
                    checked={formData.school.isPhysicalSchool}
                    onChange={() => handleSchoolChange('isPhysicalSchool', true)}
                    className="text-teal-600"
                  />
                  Physical School
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="radio"
                    name="schoolType"
                    checked={!formData.school.isPhysicalSchool}
                    onChange={() => handleSchoolChange('isPhysicalSchool', false)}
                    className="text-teal-600"
                  />
                  Online Only
                </label>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <input
                type="text"
                value={formData.school.address}
                onChange={(e) => handleSchoolChange('address', e.target.value)}
                placeholder="Address"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <select
                value={formData.school.country}
                onChange={(e) => handleSchoolChange('country', e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select Country</option>
                {getCountryNames().map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <input
                type="text"
                value={formData.school.city}
                onChange={(e) => handleSchoolChange('city', e.target.value)}
                placeholder="City"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="text"
                value={formData.school.zipcode}
                onChange={(e) => handleSchoolChange('zipcode', e.target.value)}
                placeholder="Zipcode"
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* Organization ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization I.D
            </label>
            <input
              type="text"
              value={formData.school.organizationId}
              onChange={(e) => handleSchoolChange('organizationId', e.target.value)}
              placeholder="Your organization ID number"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              We will ask for it later
            </p>
          </div>

          {/* Organization Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              * Organization Phone
            </label>
            <div className="flex gap-2" dir="ltr">
              <select className="w-32 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                {countries.map((country) => (
                  <option key={country.code} value={country.dialCode}>
                    {country.dialCode} ({country.code})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={formData.school.organizationPhone}
                onChange={(e) => handleSchoolChange('organizationPhone', e.target.value)}
                placeholder="For verifications, we won't spam you"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                dir="ltr"
              />
            </div>
          </div>

          {/* Organization Mail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              * Organization Mail
            </label>
            <input
              type="email"
              value={formData.school.organizationEmail}
              onChange={(e) => handleSchoolChange('organizationEmail', e.target.value)}
              placeholder="You will be given a VIS mailbox later"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              dir="ltr"
            />
          </div>

          {/* Organization Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Website
            </label>
            <input
              type="url"
              value={formData.school.organizationWebsite}
              onChange={(e) => handleSchoolChange('organizationWebsite', e.target.value)}
              placeholder="Some more links"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              dir="ltr"
            />
          </div>
        </div>
      )}

      {/* Step 3: Final Details */}
      {step === 3 && (
        <div className="space-y-6">
          {/* School Colors */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              School Colors
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.final.primaryColor}
                    onChange={(e) => handleFinalChange('primaryColor', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.final.primaryColor}
                    onChange={(e) => handleFinalChange('primaryColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Secondary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.final.secondaryColor}
                    onChange={(e) => handleFinalChange('secondaryColor', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.final.secondaryColor}
                    onChange={(e) => handleFinalChange('secondaryColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Upload Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload your school's logo
            </label>
            <div className="flex items-start gap-4">
              <label className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                formData.final.logo
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-300 hover:border-teal-500'
              }`}>
                {formData.final.logo ? (
                  <div className="flex flex-col items-center">
                    <Check className="w-8 h-8 text-teal-500 mb-2" />
                    <p className="text-sm text-teal-600 font-medium">{formData.final.logo.name}</p>
                    <p className="text-xs text-gray-500 mt-1">Click to change</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload or drag & drop</p>
                  </>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.size <= 2 * 1024 * 1024) {
                      handleFinalChange('logo', file);
                    } else if (file) {
                      alert('File size must be less than 2MB');
                    }
                  }}
                />
              </label>
              <div className="text-xs text-gray-500">
                <p>You can also do it later</p>
                <p>Supports jpg, png, svg, and webp</p>
                <p>No bigger than 2mb</p>
              </div>
            </div>
          </div>

          {/* School Ownership */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              School Ownership
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Is it a public school funded by the government or a private school?
            </p>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="radio"
                  name="ownership"
                  checked={formData.final.schoolOwnership === 'private'}
                  onChange={() => handleFinalChange('schoolOwnership', 'private')}
                  className="text-teal-600"
                />
                Private School
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="radio"
                  name="ownership"
                  checked={formData.final.schoolOwnership === 'public'}
                  onChange={() => handleFinalChange('schoolOwnership', 'public')}
                  className="text-teal-600"
                />
                Public School
              </label>
            </div>
          </div>

          {/* School Purpose */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              School Purpose
            </label>
            <p className="text-xs text-gray-500 mb-2">What is the purpose of your school?</p>
            <div className="flex flex-wrap gap-2">
              {schoolPurposeOptions.map((purpose) => (
                <button
                  key={purpose}
                  type="button"
                  onClick={() => toggleSchoolPurpose(purpose)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                    formData.final.schoolPurpose.includes(purpose)
                      ? 'bg-teal-100 text-teal-700 border-teal-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-teal-500'
                  }`}
                >
                  {purpose}
                </button>
              ))}
            </div>
          </div>

          {/* Invite Teammates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invite your teammates
            </label>
            <p className="text-xs text-gray-500 mb-3">
              From here, you can add other collaborators. They will have the same permissions you have
            </p>
            <div className="space-y-3">
              {formData.final.teammates.map((teammate, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-24">Teammates {index + 1}</span>
                  <input
                    type="email"
                    value={teammate.email}
                    onChange={(e) => updateTeammate(index, 'email', e.target.value)}
                    placeholder="Add email here"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    dir="ltr"
                  />
                  <select
                    value={teammate.role}
                    onChange={(e) => updateTeammate(index, 'role', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Editor">Editor</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeTeammate(index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTeammate}
                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                add more
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8">
        {step > 1 ? (
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-800"
          >
            Back
          </button>
        ) : (
          <div />
        )}

        {step < 3 ? (
          <button
            type="button"
            onClick={handleNext}
            className="px-8 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-8 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {isLoading ? 'Creating...' : 'Finish'}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">Or</span>
        </div>
      </div>

      {/* Google Sign Up */}
      <button
        type="button"
        onClick={handleGoogleSignUp}
        className="w-full flex items-center justify-center gap-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span className="text-gray-700 font-medium">Sign up with Google</span>
      </button>

      {/* Login Link */}
      <p className="mt-6 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
          Log In Link
        </Link>
      </p>
    </div>
  );
}
